import {DocumentsApiField, DocumentsFlowConfig, DocumentsSlotConfig, getAllDocumentsFlowSlots} from './documentsFlow';

export type IdentificationDocumentPhotos = Partial<Record<DocumentsApiField, string | null>>;

export type DocumentPhotoSlotValue = {
	photo: File | null;
	previewUrl: string | null;
};

export type DocumentsUploadValues = {
	slots: Record<string, DocumentPhotoSlotValue>;
	residenceAddress: string;
};

export const createEmptyDocumentsUploadValues = (flow: DocumentsFlowConfig): DocumentsUploadValues => {
	const slots: Record<string, DocumentPhotoSlotValue> = {};

	for (const slot of getAllDocumentsFlowSlots(flow)) {
		slots[slot.id] = {photo: null, previewUrl: null};
	}

	return {slots, residenceAddress: ''};
};

export const extractIdentificationDocumentPhotos = (
	fullInfo: {identificationDocument?: unknown} | null | undefined,
): IdentificationDocumentPhotos | null => {
	const document = fullInfo?.identificationDocument;

	if (!document || typeof document !== 'object') {
		return null;
	}

	const photos = 'photos' in document ? document.photos : null;

	if (!photos || typeof photos !== 'object') {
		return null;
	}

	return photos as IdentificationDocumentPhotos;
};

const resolveSlotPreviewUrl = (
	slot: DocumentsSlotConfig,
	values: DocumentsUploadValues,
	apiPhotos: IdentificationDocumentPhotos | null | undefined,
): string | null => {
	const localUrl = values.slots[slot.id]?.previewUrl;

	if (localUrl) {
		return localUrl;
	}

	const apiUrl = apiPhotos?.[slot.apiField];

	return typeof apiUrl === 'string' && apiUrl.length > 0 ? apiUrl : null;
};

export const resolveDocumentsPassportPreviewUrls = (
	flow: DocumentsFlowConfig,
	values: DocumentsUploadValues,
	apiPhotos?: IdentificationDocumentPhotos | null,
): string[] => {
	return flow.passport.slots
		.map((slot) => resolveSlotPreviewUrl(slot, values, apiPhotos))
		.filter((url): url is string => Boolean(url));
};

export const resolveResidenceDocumentPreviewUrl = (
	values: DocumentsUploadValues,
	apiPhotos?: IdentificationDocumentPhotos | null,
): string | null => {
	const localUrl = values.slots.residenceDocument?.previewUrl;

	if (localUrl) {
		return localUrl;
	}

	const apiUrl = apiPhotos?.residenceDocument;

	return typeof apiUrl === 'string' && apiUrl.length > 0 ? apiUrl : null;
};

/** @deprecated Use resolveDocumentsPassportPreviewUrls */
export const getDocumentsUploadPreviewUrls = (
	flow: DocumentsFlowConfig,
	values: DocumentsUploadValues,
): string[] => {
	return resolveDocumentsPassportPreviewUrls(flow, values);
};

/** Слот заполнен: выбран новый файл или подставлено ранее загруженное фото с бэка. */
const isDocumentSlotFilled = (value: DocumentPhotoSlotValue | undefined): boolean => {
	return Boolean(value?.photo || value?.previewUrl);
};

/** Подставляет фото из GET /identification/info в пустые слоты (без файла и без превью). */
export const applyApiPhotosToDocumentsUpload = (
	flow: DocumentsFlowConfig,
	values: DocumentsUploadValues,
	apiPhotos: IdentificationDocumentPhotos | null | undefined,
): DocumentsUploadValues => {
	if (!apiPhotos) {
		return values;
	}

	let changed = false;
	const slots = {...values.slots};

	for (const slot of getAllDocumentsFlowSlots(flow)) {
		const current = slots[slot.id];
		const apiUrl = apiPhotos[slot.apiField];

		if (current?.photo || current?.previewUrl || typeof apiUrl !== 'string' || !apiUrl) {
			continue;
		}

		slots[slot.id] = {photo: null, previewUrl: apiUrl};
		changed = true;
	}

	return changed ? {...values, slots} : values;
};

export const hasNewDocumentPhotos = (slots: DocumentsSlotConfig[], values: DocumentsUploadValues): boolean => {
	return slots.some((slot) => Boolean(values.slots[slot.id]?.photo));
};

export const isDocumentPhotosStepValid = (
	flow: DocumentsFlowConfig,
	values: DocumentsUploadValues,
): boolean => {
	const filledCount = flow.passport.slots.filter((slot) =>
		isDocumentSlotFilled(values.slots[slot.id]),
	).length;
	const minFilled = flow.passport.minFilledSlots ?? flow.passport.slots.length;

	return filledCount >= minFilled;
};

export const isResidenceDocumentStepValid = (
	flow: DocumentsFlowConfig,
	values: DocumentsUploadValues,
): boolean => {
	if (!flow.residence) {
		return true;
	}

	const hasPhoto = isDocumentSlotFilled(values.slots.residenceDocument);
	const hasAddress = !flow.residence.requireAddress || values.residenceAddress.trim().length > 0;

	return hasPhoto && hasAddress;
};

export const isDocumentsFlowValid = (flow: DocumentsFlowConfig, values: DocumentsUploadValues): boolean => {
	return isDocumentPhotosStepValid(flow, values) && isResidenceDocumentStepValid(flow, values);
};

export const buildDocumentsUploadFormData = (
	slots: DocumentsSlotConfig[],
	values: DocumentsUploadValues,
): FormData => {
	const formData = new FormData();

	for (const slot of slots) {
		const photo = values.slots[slot.id]?.photo;

		if (photo) {
			formData.append(slot.apiField, photo);
		}
	}

	return formData;
};

export const buildDocumentsFlowPassportFormData = (
	flow: DocumentsFlowConfig,
	values: DocumentsUploadValues,
): FormData => {
	return buildDocumentsUploadFormData(flow.passport.slots, values);
};

export const buildDocumentsFlowResidenceFormData = (
	flow: DocumentsFlowConfig,
	values: DocumentsUploadValues,
): FormData => {
	if (!flow.residence) {
		return new FormData();
	}

	return buildDocumentsUploadFormData(
		[{id: 'residenceDocument', label: '', apiField: 'residenceDocument'}],
		values,
	);
};
